import { Controller, useForm, useFormContext } from 'react-hook-form';
import { DatePicker, Input, UISelect } from '../common/forms';
import React, { useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { successMessage } from '~/utils/toasts';

import Axios from '~/utils/axios';
import CustomInput from '../common/forms/custom-input';
import { DateTime } from 'luxon';
import { File } from '~/components/common/forms';
import InputMask from 'react-input-mask';
import { RequestFooter } from './request-footer';
import { sentryCaptureException } from '~/errors/sentry';
import { useRouter } from 'next/router';
import { toBase64 } from '~/utils/to-base-64';
import { renderErrorsToToaster } from './render-error-to-toaster';
import { isBase64 } from '~/utils/isBase64';
import PhonePicker from '../common/forms/phone-picker';
import { isBase64Encoded } from '~/utils/isBase64Encoded';

export const VisaInformation = ({ countriesList }: any) => {
   const { translate, lang } = useTranslate();
   const router = useRouter();
   const [loading, setLoading] = useState(false);
   const { id, applicationNo } = router.query;

   const { reset, getValues } = useFormContext<any>();
   const formValues = getValues();

   const {
      register,
      control,
      formState: { errors },
      handleSubmit,
      setValue,
      setError,
      watch,
   } = useForm<any>({
      defaultValues: {
         ...formValues,
      },
   });
   const degreeOptions = [
      {
         value: 'NO_EDUCATION',
         label: translate({ id: 'visasManager:no education' }),
      },
      {
         value: 'PRIMARY',
         label: translate({ id: 'visasManager:primary' }),
      },
      {
         value: 'SECONDARY',
         label: translate({ id: 'visasManager:secondary' }),
      },
      {
         value: 'BACHELOR',
         label: translate({ id: 'visasManager:bachelor' }),
      },
      {
         value: 'HIGHER_DEGREE',
         label: translate({ id: 'visasManager:higher degree' }),
      },
      {
         value: 'OTHERS',
         label: translate({ id: 'visasManager:others' }),
      },
   ];

   const PassportTypeOptions = [
      {
         value: 'NORMAL',
         label: translate({ id: 'visasManager:normal' }),
      },
      {
         value: 'DIPLOMATIC',
         label: translate({ id: 'visasManager:diplomatic' }),
      },
      {
         value: 'TRAVEL_DOCUMENT',
         label: translate({ id: 'visasManager:travel document' }),
      },
      {
         value: 'UN_PASSPORT',
         label: translate({ id: 'visasManager:un passport' }),
      },
      {
         value: 'PRIVATE_PASSPORT',
         label: translate({ id: 'visasManager:private passport' }),
      },
      {
         value: 'OTHERS',
         label: translate({ id: 'visasManager:others' }),
      },
   ];

   const submitForm = async (values: any) => {
      setLoading(true);
      const passportCopyFile = values.passportCopy?.[0];
      const personalPhotoFile = values.personalPhoto?.[0];
      let passportCopy;
      let personalPhoto;
      if (passportCopyFile || values.passportCopy) {
         passportCopy =
            passportCopyFile &&
            (isBase64(values?.passportCopy) || isBase64Encoded(values?.passportCopy))
               ? values?.passportCopy
               : await toBase64(passportCopyFile);
      } else {
         setError('passportCopy', {
            message: translate({ id: 'validation:required' }),
         });
         setLoading(false);
      }

      if (personalPhotoFile || values.personalPhoto) {
         personalPhoto =
            personalPhotoFile &&
            (isBase64(values?.personalPhoto) || isBase64Encoded(values?.personalPhoto))
               ? values?.personalPhoto
               : await toBase64(personalPhotoFile);
      } else {
         setError('personalPhoto', {
            message: translate({ id: 'validation:required' }),
         });
         setLoading(false);
      }

      const body = {
         visitors: [{ ...values, passportCopy, personalPhoto }],
         current_step: 'applicant_visa',
      };
      try {
         await Axios.put(`/dhamen/v1/visa-request/${id}/visitors/${applicationNo}`, body);
         reset({ ...getValues(), ...values });
         successMessage(translate({ id: 'visasManager:submit message' }));
         router.push(
            `/${lang}/umrah-requests/${id}/add-applicant/${applicationNo}/edit/additional-documents`
         );
      } catch (error: any) {
         console.log(error);
         sentryCaptureException(error);
         renderErrorsToToaster(error?.response?.data);
      }
      setLoading(false);
   };

   return (
      <>
         <div>
            <div className="mb-8 text-xl font-bold text-primary">
               <Translate id="visasManager:visa information" />
            </div>

            <div className="w-full">
               <form onSubmit={handleSubmit(submitForm)}>
                  <div className="mb-4 text-xl font-bold">
                     <Translate id="visasManager:documents" />
                  </div>
                  <div className="mb-5">
                     <Input
                        id="passportNo"
                        label={<Translate id="visasManager:passport number" />}
                        placeholder={translate({ id: 'visasManager:enter passport number' })}
                        isRequired
                        error={errors.passportNo?.message}
                        {...register('passportNo', {
                           required: translate({ id: 'validation:required' }),
                           pattern: {
                              value: /^(?=.*[A-Za-z])[A-Za-z0-9]{1,10}$/, // This pattern allows English letters and numbers and enforces a length of up to 10 characters and require at least one character
                              message: translate({ id: 'validation:passport number format' }),
                           },
                           minLength: {
                              value: 3,
                              message: translate({
                                 id: 'validation:min characters',
                                 values: {
                                    count: String(3),
                                 },
                              }),
                           },
                        })}
                        description={translate({ id: 'visasManager:passport number description' })}
                     />
                  </div>
                  <div className="mb-5">
                     <Controller
                        name="passportType"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select passport type',
                              })}
                              label={translate({
                                 id: 'visasManager:passport type',
                              })}
                              id="passportType"
                              value={PassportTypeOptions.find(opt => opt.value === value)}
                              options={PassportTypeOptions}
                              error={errors.passportType?.message}
                              onChange={({ value }: any) => onChange(value)}
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="flex flex-col items-start gap-5 mb-5 lg:flex-row">
                     <Input
                        id="passportIssuePlace"
                        label={translate({
                           id: 'visasManager:passport issue place',
                        })}
                        groupClassName="w-full"
                        isRequired
                        error={errors.passportIssuePlace?.message}
                        {...register('passportIssuePlace', {
                           required: translate({ id: 'validation:required' }),
                           pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: translate({ id: 'validation:letters only' }),
                           },
                           maxLength: {
                              value: 20,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(20),
                                 },
                              }),
                           },
                        })}
                     />

                     <Controller
                        name="passportIssueCountry"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              label={translate({
                                 id: 'visasManager:passport issue country',
                              })}
                              id="passportIssueCountry"
                              value={countriesList?.find((opt: any) => opt.value === value)}
                              options={countriesList}
                              error={errors.passportIssueCountry?.message}
                              onChange={({ value }: any) => onChange(value)}
                              groupClassName="w-full"
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="flex flex-col items-start gap-5 mb-5 lg:flex-row">
                     <Controller
                        control={control}
                        name="passportIssueDate"
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <DatePicker
                              placeholderText={translate({
                                 id: 'common:date',
                              })}
                              id="passportIssueDate"
                              label={translate({ id: 'visasManager:passport issue date' })}
                              value={value?.split?.('T')?.[0] || value}
                              error={errors.passportIssueDate?.message}
                              onChange={val => {
                                 const d = DateTime.fromJSDate(val as Date);
                                 onChange(d.toFormat('yyyy-MM-dd'));
                              }}
                              maxDate={new Date(new Date().getTime() - 24 * 60 * 60 * 1000)}
                              groupClassName="w-full"
                              isRequired
                           />
                        )}
                     />
                     <Controller
                        control={control}
                        name="passportExpiryDate"
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <DatePicker
                              placeholderText={translate({
                                 id: 'common:date',
                              })}
                              id="passportExpiryDate"
                              label={translate({ id: 'visasManager:passport expiry date' })}
                              value={value?.split?.('T')?.[0] || value}
                              error={errors.passportExpiryDate?.message}
                              onChange={val => {
                                 const d = DateTime.fromJSDate(val as Date);
                                 onChange(d.toFormat('yyyy-MM-dd'));
                              }}
                              minDate={DateTime.local().plus({ days: 180 }).toJSDate()}
                              groupClassName="w-full"
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <Controller
                        control={control}
                        name="arrivalTicketReservationNumber"
                        rules={{
                           validate: value => value > 0 || translate({ id: 'validation:required' }),
                        }}
                        render={({ field: { onBlur, onChange, ref, value } }) => (
                           <InputMask
                              mask="99999999999999999999"
                              maskChar=""
                              onChange={onChange}
                              onBlur={onBlur}
                              defaultValue={value}>
                              {(props: any) => (
                                 <CustomInput
                                    {...props}
                                    defaultValue={value}
                                    inputMode="numeric"
                                    id="arrivalTicketReservationNumber"
                                    label={translate({
                                       id: 'visasManager:number reservation arrival ticket',
                                    })}
                                    placeholder={translate({
                                       id: 'visasManager:enter number reservation arrival ticket',
                                    })}
                                    isRequired
                                    error={errors.arrivalTicketReservationNumber?.message}
                                    ref={ref}
                                 />
                              )}
                           </InputMask>
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <Controller
                        control={control}
                        name="returnTicketReservationNumber"
                        rules={{
                           validate: value => value > 0 || translate({ id: 'validation:required' }),
                        }}
                        render={({ field: { onBlur, onChange, ref, value } }) => (
                           <InputMask
                              mask="99999999999999999999"
                              maskChar=""
                              onChange={onChange}
                              onBlur={onBlur}
                              defaultValue={value}>
                              {(props: any) => (
                                 <CustomInput
                                    {...props}
                                    defaultValue={value}
                                    inputMode="numeric"
                                    id="returnTicketReservationNumber"
                                    label={translate({
                                       id: 'visasManager:number reservation return ticket',
                                    })}
                                    placeholder={translate({
                                       id: 'visasManager:enter number reservation return ticket',
                                    })}
                                    isRequired
                                    error={errors.returnTicketReservationNumber?.message}
                                    ref={ref}
                                 />
                              )}
                           </InputMask>
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <File
                        id="passportCopy"
                        label={
                           <div className="flex items-center gap-1 font-semibold sm:text-base">
                              <div>
                                 <Translate id={'visasManager:upload passport photo'} />
                              </div>
                           </div>
                        }
                        {...register('passportCopy')}
                        groupClassName="!mb-8 col-span-2 flex flex-col justify-between"
                        error={(errors.passportCopy as any)?.message}
                        setError={(message: string) => setError('passportCopy', { message })}
                        onFileDelete={() => {
                           setValue('passportCopy', undefined);
                        }}
                        defaultImage={getValues('passportCopy') || formValues?.passportCopy || ''}
                        min_size_limit={0.0033}
                        size_limit={0.1}
                        size_limit_type="KB"
                        // dimensions={{ width: 200, height: 200 }}
                        is_required
                        isEncodedBase64={Boolean(isBase64Encoded(watch('passportCopy')))}
                        accept="image/jpeg,image/gif,image/png,image/x-eps"
                        description={
                           <ul className="list-disc list-inside">
                              <li>
                                 {translate({
                                    id: 'validation:max file size with type',
                                    values: {
                                       amount: '100',
                                       type: 'kb',
                                    },
                                 })}
                              </li>
                              <li>
                                 {translate({
                                    id: 'validation:min file size with type',
                                    values: {
                                       amount: '3.3',
                                       type: 'kb',
                                    },
                                 })}
                              </li>
                              {/* <li>
                                 <Translate
                                    id="visasManager:upload dimensions"
                                    values={{
                                       width: '200',
                                       height: '200',
                                    }}
                                 />
                              </li> */}
                           </ul>
                        }
                     />
                  </div>
                  <div className="mb-5">
                     <File
                        id="personalPhoto"
                        label={
                           <div className="flex items-center gap-1 font-semibold sm:text-base">
                              <div>
                                 <Translate id={'visasManager:upload applicant photo'} />
                              </div>
                           </div>
                        }
                        {...register('personalPhoto')}
                        defaultImage={getValues('personalPhoto') || formValues?.personalPhoto || ''}
                        groupClassName="!mb-8 col-span-2 flex flex-col justify-between"
                        error={(errors.personalPhoto as any)?.message}
                        setError={(message: string) => setError('personalPhoto', { message })}
                        onFileDelete={() => {
                           setValue('personalPhoto', undefined);
                        }}
                        min_size_limit={0.0033}
                        size_limit={0.018}
                        size_limit_type="KB"
                        dimensions={{ width: 200, height: 200 }}
                        is_required
                        isEncodedBase64={Boolean(isBase64Encoded(watch('personalPhoto')))}
                        accept="image/jpeg,image/gif,image/png,image/x-eps"
                        description={
                           <ul className="list-disc list-inside">
                              <li>
                                 {translate({
                                    id: 'validation:max file size with type',
                                    values: {
                                       amount: '18',
                                       type: 'kb',
                                    },
                                 })}
                              </li>
                              <li>
                                 {translate({
                                    id: 'validation:min file size with type',
                                    values: {
                                       amount: '3.3',
                                       type: 'kb',
                                    },
                                 })}
                              </li>
                              <li>
                                 <Translate
                                    id="visasManager:upload dimensions"
                                    values={{
                                       width: '200',
                                       height: '200',
                                    }}
                                 />
                              </li>
                           </ul>
                        }
                     />
                  </div>
                  <div className="mt-8 mb-4 text-xl font-bold">
                     <Translate id="visasManager:address" />
                  </div>

                  <div className="mb-5">
                     <div className="mb-5">
                        <Input
                           label={<Translate id="visasManager:mobile number" />}
                           placeholder={translate({
                              id: 'visasManager:enter mobile number',
                           })}
                           labelClassName="font-bold"
                           isRequired
                           error={errors.phone_number?.message}
                           {...register('phone_number', {
                              validate: value =>
                                 value > 0 || translate({ id: 'validation:required' }),
                              minLength: {
                                 value: 10 - getValues('country_code')?.length || 9,
                                 message: translate({
                                    id: 'validation:min characters',
                                    values: {
                                       count: String(
                                          String(10 - getValues('country_code')?.length || 9)
                                       ),
                                    },
                                 }),
                              },
                              maxLength: {
                                 value: 16 - getValues('country_code')?.length || 15,
                                 message: translate({
                                    id: 'validation:max characters',
                                    values: {
                                       count: String(16 - getValues('country_code')?.length || 15),
                                    },
                                 }),
                              },
                           })}
                           type="number"
                           inputMode="numeric"
                           inputWrapperClassName="relative"
                           prepend={
                              <div className="flex items-center justify-center w-[120px] h-full -ml-2 -mr-6 bg-gray-100 ltr:rounded-l-md rtl:rounded-r-md">
                                 <PhonePicker
                                    placeholder="XXX"
                                    onCountrySelected={({ phone }) =>
                                       setValue('country_code', !phone ? '' : `+${phone}`, {
                                          shouldValidate: true,
                                       })
                                    }
                                    value={getValues('country_code') || null}
                                 />
                              </div>
                           }
                        />
                     </div>
                  </div>
                  <div className="mb-5">
                     <Input
                        id="homeAddress"
                        label={<Translate id="visasManager:full address" />}
                        placeholder={translate({ id: 'visasManager:enter full address' })}
                        isRequired
                        error={errors.homeAddress?.message}
                        {...register('homeAddress', {
                           // required: translate({ id: 'validation:required' }),
                           maxLength: {
                              value: 100,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(100),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
                  <div className="mb-5">
                     <Input
                        id="addressCity"
                        placeholder={translate({
                           id: 'visasManager:select city',
                        })}
                        label={translate({
                           id: 'visasManager:city',
                        })}
                        isRequired
                        error={errors.addressCity?.message}
                        {...register('addressCity', {
                           // required: translate({ id: 'validation:required' }),
                           maxLength: {
                              value: 20,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(20),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>

                  <div className="flex flex-col items-start gap-5 mb-5 lg:flex-row">
                     <Controller
                        control={control}
                        name="zipCode"
                        rules={{
                           // required: translate({ id: 'validation:required' }),
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        }}
                        render={({ field: { onBlur, onChange, ref, value } }) => (
                           <InputMask
                              mask="999999999999999"
                              maskChar=""
                              onChange={onChange}
                              onBlur={onBlur}
                              defaultValue={value}>
                              {(props: any) => (
                                 <CustomInput
                                    {...props}
                                    defaultValue={value}
                                    inputMode="numeric"
                                    id="zipCode"
                                    label={<Translate id="visasManager:zip code" />}
                                    placeholder={translate({ id: 'visasManager:enter zip code' })}
                                    isRequired
                                    error={errors.zipCode?.message}
                                    ref={ref}
                                    groupClassName="w-full"
                                 />
                              )}
                           </InputMask>
                        )}
                     />
                     <Controller
                        name="addressCountry"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select country',
                              })}
                              label={translate({
                                 id: 'visasManager:country',
                              })}
                              id="addressCountry"
                              value={countriesList?.find((opt: any) => opt.value === value)}
                              options={countriesList}
                              error={errors.addressCountry?.message}
                              onChange={({ value }: any) => onChange(value)}
                              isRequired
                              groupClassName="w-full"
                           />
                        )}
                     />
                  </div>
                  {/* <div className="mb-5">
                     <Input
                        id="request_type"
                        label={<Translate id="visasManager:request type" />}
                        placeholder={translate({ id: 'visasManager:request type' })}
                        isRequired
                        error={errors.request_type?.message}
                        {...register('request_type', {
                           required: translate({ id: 'validation:required' }),
                        })}
                     />
                  </div> */}
                  <div className="mt-8 mb-4 text-xl font-bold">
                     <Translate id="visasManager:education" />
                  </div>
                  <div className="mb-5">
                     <Controller
                        name="degree"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select degree',
                              })}
                              label={translate({
                                 id: 'visasManager:degree',
                              })}
                              id="degree"
                              value={degreeOptions.find(opt => opt.value === value)}
                              options={degreeOptions}
                              error={errors.degree?.message}
                              onChange={({ value }: any) => onChange(value)}
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <Input
                        id="profession"
                        label={<Translate id="visasManager:profession" />}
                        placeholder={translate({ id: 'visasManager:enter profession' })}
                        isRequired
                        error={errors.profession?.message}
                        {...register('profession', {
                           required: translate({ id: 'validation:required' }),
                           maxLength: {
                              value: 20,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(20),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
                  <div className="mb-5">
                     <Input
                        id="jobTitle"
                        label={<Translate id="visasManager:job or relation" />}
                        placeholder={translate({ id: 'visasManager:enter job or relation' })}
                        isRequired
                        error={errors.jobTitle?.message}
                        {...register('jobTitle', {
                           required: translate({ id: 'validation:required' }),
                           maxLength: {
                              value: 20,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(20),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
               </form>
            </div>
         </div>
         <RequestFooter
            fwdBtnHandler={handleSubmit(submitForm)}
            cancelBtnHandler={() =>
               router.push(
                  `/${lang}/umrah-requests/${id}/add-applicant/${applicationNo}/edit/applicant-information`
               )
            }
            loading={loading}
         />
      </>
   );
};
